import React from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

// TiltMap3D — the "Uber-killer" pixel.
// Google Maps vector-basemap with tilt + heading. Used ONLY on the Rider PWA
// active-ride screen where the driver's marker matters most. Compose/quote
// screens keep the lightweight Leaflet map. Keeps ease-out-cubic marker lerp
// so the vehicle doesn't teleport between polls.

const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "";  // optional vector styleset

const CONTAINER_STYLE = { width: "100%", height: "260px", borderRadius: "16px" };

// Tilt only works on vector maps (Map ID required). If no Map ID is set, we
// still render but the map is flat — no crash.
const DEFAULT_TILT = 55;
const DEFAULT_ZOOM = 17;

export default function TiltMap3D({ pickup, dropoff, driverPosition, driverName }) {
  const { isLoaded } = useJsApiLoader({
    id: "astat-google-map-3d",
    googleMapsApiKey: MAP_KEY,
  });

  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const animRef = React.useRef(null);
  const fromRef = React.useRef(null);
  const toRef = React.useRef(null);
  const startedRef = React.useRef(0);

  // Smooth marker lerp — matches SmoothDriverMarker semantics.
  React.useEffect(() => {
    if (!driverPosition?.lat || !driverPosition?.lng) return;
    if (!toRef.current) {
      toRef.current = { lat: driverPosition.lat, lng: driverPosition.lng };
      if (markerRef.current) markerRef.current.setPosition(toRef.current);
      return;
    }
    if (toRef.current.lat === driverPosition.lat && toRef.current.lng === driverPosition.lng) return;
    fromRef.current = { ...toRef.current };
    toRef.current = { lat: driverPosition.lat, lng: driverPosition.lng };
    startedRef.current = performance.now();
    const DURATION = 2500;
    const step = (now) => {
      const t = Math.min(1, (now - startedRef.current) / DURATION);
      const e = 1 - Math.pow(1 - t, 3);
      const lat = fromRef.current.lat + (toRef.current.lat - fromRef.current.lat) * e;
      const lng = fromRef.current.lng + (toRef.current.lng - fromRef.current.lng) * e;
      if (markerRef.current) markerRef.current.setPosition({ lat, lng });
      if (t < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [driverPosition?.lat, driverPosition?.lng]);

  // Frame the map on the driver if present, else the pickup, else the dropoff.
  const center = React.useMemo(() => {
    if (driverPosition?.lat) return { lat: driverPosition.lat, lng: driverPosition.lng };
    if (pickup?.lat) return { lat: pickup.lat, lng: pickup.lng };
    if (dropoff?.lat) return { lat: dropoff.lat, lng: dropoff.lng };
    return { lat: 41.8781, lng: -87.6298 };  // Chicago fallback
  }, [driverPosition?.lat, driverPosition?.lng, pickup?.lat, dropoff?.lat]);

  const onLoad = React.useCallback((map) => {
    mapRef.current = map;
    // Apply tilt + heading imperatively — Google Maps supports 3D vector tilt.
    try {
      map.setTilt(DEFAULT_TILT);
      map.setHeading(0);
    } catch (e) {
      // Non-vector map (no Map ID) — tilt silently ignored.
    }
  }, []);

  if (!MAP_KEY) {
    return (
      <div style={CONTAINER_STYLE} className="bg-slate-100" />
    );
  }
  if (!isLoaded) {
    return (
      <div style={CONTAINER_STYLE} className="bg-slate-100" />
    );
  }

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    tilt: DEFAULT_TILT,
    heading: 0,
    // mapId enables vector rendering + 3D tilt. Without one, tilt is ignored
    // and the map falls back to raster (still usable, still fast).
    ...(MAP_ID ? { mapId: MAP_ID } : {}),
    gestureHandling: "greedy",
  };

  const pinCar = {
    path: "M12 2C7 2 3 3 3 8v6a1 1 0 0 0 1 1h1a2 2 0 0 0 4 0h6a2 2 0 0 0 4 0h1a1 1 0 0 0 1-1V8c0-5-4-6-9-6z",
    fillColor: "#2B4C3B",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.4,
    anchor: { x: 12, y: 12 },
  };
  const pinPickup = { path: "M12 0C5 0 0 5 0 12c0 8 12 24 12 24s12-16 12-24c0-7-5-12-12-12z", fillColor: "#3A7BD5", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2, scale: 0.9, anchor: { x: 12, y: 36 } };
  const pinDropoff = { path: "M12 0C5 0 0 5 0 12c0 8 12 24 12 24s12-16 12-24c0-7-5-12-12-12z", fillColor: "#B04B2E", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2, scale: 0.9, anchor: { x: 12, y: 36 } };

  return (
    <div data-testid="tilt-map-3d">
      <GoogleMap
        mapContainerStyle={CONTAINER_STYLE}
        center={center}
        zoom={DEFAULT_ZOOM}
        options={mapOptions}
        onLoad={onLoad}
      >
        {pickup?.lat && <Marker position={{ lat: pickup.lat, lng: pickup.lng }} icon={pinPickup} title="Pickup" />}
        {dropoff?.lat && <Marker position={{ lat: dropoff.lat, lng: dropoff.lng }} icon={pinDropoff} title="Dropoff" />}
        {driverPosition?.lat && (
          <Marker
            position={{ lat: driverPosition.lat, lng: driverPosition.lng }}
            icon={pinCar}
            title={driverName || "Driver"}
            onLoad={(m) => { markerRef.current = m; }}
          />
        )}
      </GoogleMap>
    </div>
  );
}
